import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  lazy,
  Suspense
} from 'react'
import type { ReactNode } from 'react'
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom'
import { message, Result, Button, Spin } from 'antd'
import * as Sentry from '@sentry/react'

// 全局常挂组件保持静态导入（体量小，随 App 首帧渲染）
import AgentCustomerDrawer from './pages/agentCustomer'
import ClawBetaApprovedModal from './pages/todayClaw/components/ClawBetaApprovedModal'
import AIClientUpgradeModal from './pages/components/AIClientUpgradeModal'
import { shouldShowAIClientUpgradeModal } from './utils/aiClientUpgrade'
import {
  shouldShowClawBetaApprovedModal,
  markClawBetaApprovedShown
} from './pages/todayClaw/clawBetaModalConfig'
import { post as personalPost } from './pages/my-space/utils/request'
import { setSpaceOpen } from './slices/mySpace/personalSpaceSlice'
import { abtest } from './utils/sensors'
import actionTypes from './constants/actionTypes'
import {
  fetchUser,
  fetchConfig,
  getOSSToken,
  fetchWechatOAuth2
} from './actions/main'
import {
  setGroupId,
  fetchGroupInfo,
  fetchGroupDeparment,
  fetchGroupCustomerConfig
} from './actions/group'
import parseQueryString from './utils/parseQueryString'
import sensors from './utils/sensors'
import { capturePromiseException } from './utils/sentryUtils'
import AuthPolicyWrapper from './pages/components/AuthPolicyWrapper'
import CONFIG from './constants/config'
import { useAppDispatch, useAppSelector } from './app/hooks'
import { useThreeInspectionAccess } from './pages/threeInspectionReport/hooks/useThreeInspectionAccess'
import {
  isWorkBuddyAuthorizePath,
  isWorkBuddyOAuthLoginPath,
  parseWorkBuddyOAuthReturnURL,
  registerWorkBuddyOAuthRedirectAttempt
} from './pages/workBuddyAuthorize/workBuddyAuthorize.utils'
import type { WorkBuddyOAuthLoginErrorReason } from './pages/workBuddyAuthorize/OAuthLoginError'

// ─── 页面组件一律 React.lazy 路由级拆包 ─────────────────────────────
// 每个页面（连同其独有依赖）在首次命中路由时才下载对应 chunk，主包只保留
// App 壳与全局链路（登录编排、埋点、Sentry、store）。新增路由请沿用
// lazy(() => import(...)) 写法，不要把页面静态 import 回主包。
// BrowserRouter 已开 v7_startTransition：站内切路由时保留旧页面直到新
// chunk 就绪，Suspense fallback 基本只在首次进站时可见。
const Login = lazy(() => import('./pages/login'))
const WorkBuddyAuthorize = lazy(() => import('./pages/workBuddyAuthorize'))
const WorkBuddyOAuthLoginError = lazy(
  () => import('./pages/workBuddyAuthorize/OAuthLoginError')
)
const GeoData = lazy(() => import('./pages/geoData'))
const ChooseGroup = lazy(() => import('./pages/chooseGroup'))
const MainWrapper = lazy(() => import('./pages/mainWrapper'))
const DataStatistics = lazy(() => import('./pages/dataStatistics'))
const AIPhotoKing = lazy(() => import('./pages/ai-photo-king'))
const PhotoTaskFlow = lazy(() => import('./pages/photoTaskFlow'))
const PhotoTaskFlowManage = lazy(() => import('./pages/photoTaskFlow/manage'))
const AIClassifyFolder = lazy(() => import('./pages/AIClassifyFolder'))
const AIClassifyFolderDetail = lazy(
  () => import('./pages/AIClassifyFolder/Detail')
)
const AIClassifyUnclassified = lazy(
  () => import('./pages/AIClassifyFolder/Unclassified')
)
const MingyangAi = lazy(() => import('./pages/mingyangAi'))
const DeleteRecords = lazy(() => import('./pages/ai-photo-king/DeleteRecords'))
const DownloadFailedPhotos = lazy(() => import('./pages/downloadFailedPhotos'))
const Pathway = lazy(() => import('./pages/pathway'))
const Authority = lazy(() => import('./pages/authority'))
const Department = lazy(() => import('./pages/department/index'))
const TeamBatchImport = lazy(() => import('./pages/member/TeamBatchImport'))
const OrgArcBatchImort = lazy(() => import('./pages/member/OrgArcBatchImort'))
const Admin = lazy(() => import('./pages/admin'))
const Jigsaw = lazy(() => import('./pages/jigsaw'))
const AIPhoto = lazy(() => import('./pages/aiPhoto'))
const AIPhotoTags = lazy(() => import('./pages/aiPhotoTags'))
const PhotoLocation = lazy(() => import('./pages/photoLocation'))
const AiSearchPhoto = lazy(() => import('./pages/aiSearchPhoto/index'))
const AttendanceDaily = lazy(() => import('./pages/attendance/daily'))
const AttendancePeriod = lazy(() => import('./pages/attendance/period'))
const AttendanceSetting = lazy(() => import('./pages/attendance/setting'))
const AttendanceSettings = lazy(() => import('./pages/attendance/ruleSetting'))
const AttendanceCalendar = lazy(() => import('./pages/attendance/calendar'))
const AttendanceExportList = lazy(() => import('./pages/attendance/exportList'))
const PhotoWatermark = lazy(() => import('./pages/photoWatermark'))
const WatermarkList = lazy(() => import('./pages/watermark/list/index'))
const WatermarkEditor = lazy(() => import('./pages/watermark/editor'))
const WatermarkBatchEditor = lazy(() => import('./pages/watermark/batchEditor'))
const AddBatchEditPhotos = lazy(
  () => import('./pages/watermark/addPhotos/index')
)
const WatermarkBatchEditorTest = lazy(
  () => import('./pages/watermark/batchEditor/test')
)
const AIRecognition = lazy(() => import('./pages/aiRecognition'))
const AIRecognitionExportList = lazy(
  () => import('./pages/aiRecognition/exportList')
)
const AIDuplicatePhoto = lazy(() => import('./pages/aiDuplicatePhoto'))
const TodayClaw = lazy(() => import('./pages/todayClaw'))
const QA = lazy(() => import('./pages/q&a'))
const UserManual = lazy(() => import('./pages/userManual'))
const Customers = lazy(() => import('./pages/customer/customers'))
const CustomerVisitRecords = lazy(() => import('./pages/customer/visitRecords'))
const VerifyAuth = lazy(() => import('./pages/verifyAuth/index'))
const VerifyProcess = lazy(() => import('./pages/verifyAuth/process'))
const VerifyResult = lazy(() => import('./pages/verifyAuth/result'))
const VerifyManualReview = lazy(
  () => import('./pages/verifyAuth/manualReview/index')
)
const PhotoFolderAddPhoto = lazy(() => import('./pages/photoFolders/AddPhoto'))
const PhotoPickerPage = lazy(() => import('./pages/components/PhotoPickerPage'))
const TeamOperationLog = lazy(() => import('./pages/team/Log'))
const AIClerk = lazy(() => import('./pages/aiClerk'))
const Orders = lazy(() => import('./pages/orders'))
// AiPhotoReview 已下线（master 70a77014, 2026-05-19）
const MyseSimulate = lazy(() => import('./pages/myseInspect/simulate'))
const MyseKnowledgeBase = lazy(
  () => import('./pages/myseInspect/knowledgeBase')
)
const MyseCheckRecords = lazy(() => import('./pages/myseInspect'))
const MyseProjects = lazy(() => import('./pages/myseInspect/projects'))
const MyseDownloads = lazy(() => import('./pages/myseInspect/downloads'))
const NativeClawProPage = lazy(() => import('./pages/nativeClawPro'))
// 供桌面客户端 iframe 内嵌的开通会员页
const EmbedVipPage = lazy(() => import('./pages/embedVip'))
const Rectify = lazy(() => import('./pages/rectify'))
const AISecurityLedger = lazy(() => import('./pages/aiSecurity'))
const MemberLocation = lazy(() => import('./pages/memberLocation'))
const MemberTrack = lazy(() => import('./pages/memberTrack'))
const CreditHome = lazy(() => import('./pages/credit/pages'))
const CreditReport = lazy(() => import('./pages/credit/pages/CreditReport'))
const MySpaceRoutes = lazy(() => import('./pages/my-space/Routes'))
const Privileges = lazy(() => import('./pages/privileges'))
const ThreeInspectionTemplates = lazy(
  () => import('./pages/threeInspectionReport/templates')
)
const ThreeInspectionEditor = lazy(
  () => import('./pages/threeInspectionReport/editor')
)

// 路由门禁组件定义在模块作用域（而非 renderContent 内部）。
// 若定义在 renderContent 内，App 每次重渲染都会生成新的组件「类型」，
// React 会把被它包裹的路由整棵子树卸载再重挂载——例如考勤「筛选成员」弹窗里
// 切「仅显示当前在团队的成员」开关会改 group.onlyInGroup → App 重渲染 →
// 守卫身份变化 → AttendancePeriod/Filter 连同已打开的弹窗被重挂载、弹窗消失（RFCT-40）。
// 提到模块作用域后身份稳定，所需的角色/权限值通过 useAppSelector 读取（始终最新）。
// 对齐基准 origin/master：基准 App.js 用「返回元素的普通函数」AdminRoleRender 实现守卫，
// 渲染树里守卫位置直接是页面组件、身份稳定，故无此重挂载。
const NotAdminPage = () => {
  return (
    <div className="result-page-wrapper">
      <Result
        status="warning"
        title="您不是管理员，无权限查看此页面。"
        subTitle="请联系当前团队的管理员，将您设置为管理员之后查看。"
        extra={[
          <p key="0">
            （备注：在今日水印相机APP上，点击我的团队，打开成员列表页面，可以查看到当前的管理员。）
          </p>
        ]}
      />
    </div>
  )
}

const WatermarkNotAdminPage = () => {
  return (
    <div className="result-page-wrapper">
      <Result
        status="warning"
        title="您没有管理团队水印的权限，请联系主管理员。"
        subTitle={
          '在今日水印相机APP上，点击我的团队，在管理页面的"权限和人员管理"，可以查看当前的主管理员'
        }
      />
    </div>
  )
}

const FolderNoPermissionPage = (): React.ReactElement => (
  <div className="result-page-wrapper">
    <Result
      status="warning"
      title="你没有查看文件夹的权限，请联系主管理员"
      subTitle={
        '在今日水印相机 APP 上，点击我的团队，在管理页面的"权限和人员管理"，可以查看当前的主管理员'
      }
    />
  </div>
)

const AdminRoleGuard = ({
  children
}: {
  children: ReactNode
}): React.ReactElement => {
  const myRole = useAppSelector((state) => state.group.myRole)
  if (myRole > 0 && myRole < 3) {
    return <>{children}</>
  }
  return <NotAdminPage />
}

const WatermarkAdminGuard = ({
  children
}: {
  children: ReactNode
}): React.ReactElement => {
  const groupAuthPolicy = useAppSelector((state) => state.group.groupAuthPolicy)
  const isAuthPassed = groupAuthPolicy?.policyList.some(
    (item: { path: string }) =>
      item.path === '/web/workgroup/watermark/item/save'
  )
  if (isAuthPassed) {
    return <>{children}</>
  }
  return <WatermarkNotAdminPage />
}

/**
 * AI 智能分类页面门禁 · 放行条件与旧版分类文件夹列表页一致：
 * 主管理员/管理员，或团队开放了照片查看权限（viewPerm === 0）的成员；
 * 其余渲染文件夹权限门禁页。
 */
const AIClassifyFolderGuard = ({
  children
}: {
  children: ReactNode
}): React.ReactElement => {
  const myRole = useAppSelector((state) => state.group.myRole)
  const viewPerm = useAppSelector((state) => state.group.viewPerm)
  const isAdminRole =
    myRole === CONFIG.memberRoleTypes.MASTER_ADMIN ||
    myRole === CONFIG.memberRoleTypes.ADMIN
  if (isAdminRole || viewPerm === 0) {
    return <>{children}</>
  }
  return <FolderNoPermissionPage />
}

/** 旧分类文件夹详情 → AI 智能分类详情重定向，兼容历史链接/书签 */
const PhotoFolderDetailRedirect = (): React.ReactElement => {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/ai-classify/folder/${id}`} replace />
}

/**
 * 拍照流程门禁：仅对已放量团队（taskForm.enabled）放行。
 * 未放量团队即使直接输 URL / 用书签也重定向回全部照片——与侧导航入口的显隐门禁一致
 * （见 SideComponent 照片管理组过滤），未放量团队走旧的「任务表单」外链。
 * 路由在团队信息加载完成后才渲染，故此处 taskForm 已就绪，无需额外处理加载态。
 */
const TaskFlowGuard = ({
  children
}: {
  children: ReactNode
}): React.ReactElement => {
  const taskFormEnabled = useAppSelector(
    (state) => state.group.taskForm?.enabled
  )
  if (taskFormEnabled) {
    return <>{children}</>
  }
  return <Navigate to="/photo/all" replace />
}

/** 三检记录表所有页面均受远程 KV 灰度控制，直接输入 URL 也不能绕过。 */
const ThreeInspectionGuard = ({
  children
}: {
  children: ReactNode
}): React.ReactElement => {
  const canAccess = useThreeInspectionAccess()
  if (canAccess) {
    return <>{children}</>
  }
  return <Navigate to="/photo/all" replace />
}

/** 路由 chunk 加载中的兜底态（定义在模块作用域，身份稳定，避免子树重挂载） */
const RouteLoading = (): React.ReactElement => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh'
    }}
  >
    <Spin size="large" />
  </div>
)

function App() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  // Redux state
  const pathname = location.pathname
  // renderContent 和全局弹层必须随 React Router 路由变化重新计算，因此使用响应式
  // pathname；异步登录回调会在更晚执行，必须改读 window.location.pathname。
  const isWorkBuddyAuthorizePage = isWorkBuddyAuthorizePath(pathname)
  const isWorkBuddyOAuthLoginPage = isWorkBuddyOAuthLoginPath(pathname)
  // 兼容修复发布前已打开的旧 `/workbuddy/authorize?return_url=...` 页面；新请求
  // 统一进入独立 OAuth 登录页，彻底区分 Buddy code 与微信登录 code。
  const workBuddyOAuthReturnURL =
    isWorkBuddyAuthorizePage || isWorkBuddyOAuthLoginPage
      ? parseWorkBuddyOAuthReturnURL(location.search)
      : ''
  const rawWorkBuddyOAuthReturnURL = isWorkBuddyOAuthLoginPage
    ? new URLSearchParams(location.search).get('return_url')?.trim() || ''
    : ''
  const [workBuddyOAuthRedirectError, setWorkBuddyOAuthRedirectError] =
    useState<WorkBuddyOAuthLoginErrorReason | null>(null)
  // React 状态更新可能让 effect 在浏览器真正离页前再次执行。记录已处理的 URL，
  // 既避免同页重复 replace，也允许路由切换到一条全新的 OAuth 请求后正常处理。
  const workBuddyOAuthRedirectStartedURLRef = useRef('')
  const user = useAppSelector((state) => state.main.user)
  const initialRequestFinished = useAppSelector(
    (state) => state.main.initialRequestFinished
  )
  const groupName = useAppSelector((state) => state.group.groupName)
  const groupID = useAppSelector((state) => state.group.groupId)
  const teamShortId = useAppSelector(
    (state) => state.group.invite_id || state.group.groupId
  )
  const myRole = useAppSelector((state) => state.group.myRole)
  const isOnlyInGroup = useAppSelector((state) => state.group.onlyInGroup)
  const groupAuthPolicy = useAppSelector((state) => state.group.groupAuthPolicy)
  const mainConfig = useAppSelector((state) => state.main.config)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const isSpaceOpen = useAppSelector(
    (state) => state.mySpace?.personalSpace?.isSpaceOpen ?? false
  )
  // 2026-06-12 去掉 AB 实验, 全量放开 AI 照片王（/photo/all 直接走 AIPhotoKing）

  // Local state
  const [clawBetaModalClosed, setClawBetaModalClosed] = useState(false)
  // AI 版客户端老版本升级引导弹窗（展示判定见 mount effect）
  const [aiClientUpgradeVisible, setAiClientUpgradeVisible] = useState(false)
  // 个人空间信息（/web/user/space/info）就绪标志：登录后路由判定依赖个人空间
  // 是否开通，必须等该接口返回后再渲染 /choose_group 与无团队分支，避免个人空间
  // 入口闪烁、以及无团队场景过早重定向回 /choose_group（对齐 origin/master）。
  const [isSpaceInfoLoaded, setIsSpaceInfoLoaded] = useState(false)

  // Refs
  const groupIDRef = useRef(localStorage.getItem('group_id'))
  const theKingOfAIPhotosRefetchedRef = useRef(false)

  // handleAfterLogin 与 handleFetchGroupInfo 互相调用（前者按个人空间状态决定是否进
  // 团队，后者在 Login 传入 groups 时回调前者），用 ref 打破 useCallback 循环依赖。
  const handleAfterLoginRef = useRef<(groups: unknown[]) => void>(
    () => undefined
  )

  const handleFetchGroupInfo = useCallback(
    ({
      isFromBinding = false,
      groups
    }: { isFromBinding?: boolean; groups?: unknown[] } = {}) => {
      // Login 组件传入 groups 时，统一走 handleAfterLogin 判断（含个人空间）
      if (groups !== undefined) {
        handleAfterLoginRef.current(groups)
        return
      }
      const groupIDLocal =
        localStorage.getItem('group_id') || groupIDRef.current
      if (groupIDLocal) {
        dispatch(setGroupId(groupIDLocal))
        dispatch(
          fetchGroupInfo({
            group_id: groupIDLocal,
            only_in_group: isOnlyInGroup
          })
        ).catch(capturePromiseException)
        dispatch(fetchGroupDeparment({ groupID: groupIDLocal }))
        dispatch(fetchGroupCustomerConfig({ groupID: groupIDLocal }))
      } else {
        // 登录回调可能晚于发起时的 render 执行，必须读取回调执行当下的真实地址，
        // 不能使用 useLocation 在旧 render 中捕获的 pathname。
        const pathnameLocal = window.location.pathname
        // 个人空间不依赖团队，无需重定向（否则多团队/扫码登录落在 /my-space 会被
        // 无条件弹回 /choose_group，即 RFCT-43 的回弹根因）。
        if (pathnameLocal.startsWith('/my-space')) {
          return
        }
        // WorkBuddy Buddy 授权页和 MCP OAuth 登录承接页都依赖当前 URL 的一次性
        // 上下文，不能在尚未写入全局 group_id 时提前跳走。
        if (
          isWorkBuddyAuthorizePath(pathnameLocal) ||
          isWorkBuddyOAuthLoginPath(pathnameLocal)
        ) {
          return
        }
        if (/\/verifyAuth(\/[^/]+){1,3}|\/verification/.test(pathnameLocal)) {
          const { pageFrom } = parseQueryString()
          localStorage.setItem(
            'enterPage',
            pageFrom === 'officialWebsite'
              ? 'verificationOfficial'
              : 'verification'
          )
        }
        if (isFromBinding) {
          navigate('/choose_group?from=binding', { replace: true })
        } else {
          navigate('/choose_group', { replace: true })
        }
      }
    },
    [dispatch, isOnlyInGroup, navigate]
  )

  // 登录成功后：等个人空间开通状态（/web/user/space/info）返回后统一决定路由。
  // 对齐 origin/master 的 handleAfterLogin —— 区分无团队 / 单团队 / 多团队，并按
  // switching_group（主动切团队）、已选团队（刷新/新标签页）、首次登录三种情形分流；
  // 首次登录且无已选团队时，仅当当前不在 /my-space 或 WorkBuddy 授权页时才
  // 重定向到 /choose_group；授权页需要留在原路径展示可授权团队。
  const handleAfterLogin = useCallback(
    (groups: unknown[]) => {
      const teamList = (groups || []) as Array<{ group_id: string }>
      const teamCount = teamList.length

      personalPost<{ isOpen?: boolean }>('/web/user/space/info', {})
        .then((res) => {
          const isOpen = res?.isOpen === true
          dispatch(setSpaceOpen(isOpen))

          if (teamCount === 0) {
            // 无团队：renderContent 根据 isSpaceOpen 决定（个人空间 or 验真兜底）
            setIsSpaceInfoLoaded(true)
            return
          }

          // 单团队无个人空间 → 直接进团队（原有体验）
          if (teamCount === 1 && !isOpen) {
            localStorage.setItem('group_id', teamList[0].group_id)
            dispatch(setGroupId(teamList[0].group_id))
            setIsSpaceInfoLoaded(true)
            groupIDRef.current = teamList[0].group_id
            handleFetchGroupInfo()
            return
          }

          // 有个人空间 → 判断是主动切换团队、已选过团队、还是首次登录
          if (isOpen) {
            // 用户主动切换团队（侧边栏）→ 直接进目标团队
            if (sessionStorage.getItem('switching_group')) {
              sessionStorage.removeItem('switching_group')
              const storedGroupId = localStorage.getItem('group_id')
              if (storedGroupId) {
                dispatch(setGroupId(storedGroupId))
                groupIDRef.current = storedGroupId
              }
              setIsSpaceInfoLoaded(true)
              handleFetchGroupInfo()
              return
            }
            // 已选过团队（新标签页/刷新）→ 直接进该团队
            const storedGroupId = localStorage.getItem('group_id')
            if (storedGroupId) {
              dispatch(setGroupId(storedGroupId))
              groupIDRef.current = storedGroupId
              setIsSpaceInfoLoaded(true)
              handleFetchGroupInfo()
              return
            }
            // 首次登录且无已选团队 → 选择页；个人空间和 WorkBuddy 授权页放行，
            // 后者必须保留 URL 中尚未兑换的一次性授权码。
            // personalPost 已经过异步边界，这里必须读取回调完成时的真实地址。
            const currentPathname = window.location.pathname
            if (
              !currentPathname.startsWith('/my-space') &&
              !isWorkBuddyAuthorizePath(currentPathname) &&
              !isWorkBuddyOAuthLoginPath(currentPathname)
            ) {
              navigate('/choose_group', { replace: true })
            }
            setIsSpaceInfoLoaded(true)
            return
          }

          // 多团队无个人空间 → 原有逻辑（有 stored groupId 进团队，没有进选择页）
          setIsSpaceInfoLoaded(true)
          handleFetchGroupInfo()
        })
        .catch(() => {
          // 接口失败按无个人空间处理
          setIsSpaceInfoLoaded(true)
          if (teamCount === 0) return
          if (teamCount === 1) {
            localStorage.setItem('group_id', teamList[0].group_id)
            dispatch(setGroupId(teamList[0].group_id))
            groupIDRef.current = teamList[0].group_id
          }
          handleFetchGroupInfo()
        })
    },
    [dispatch, navigate, handleFetchGroupInfo]
  )

  // 把最新的 handleAfterLogin 同步到 ref，供 handleFetchGroupInfo 回调
  handleAfterLoginRef.current = handleAfterLogin

  // 1202版本更新：添加获取A/B测试配置
  const handleFetchABTestConfig = useCallback(() => {
    // AI 填表员入口已全量放开，移除原 AI_form_filling_master_abtest 拉取（追平 #726）。
    // newWebSearchAB 实验已全量（恒走 photov2/新路径），移除 AB 拉取与 localStorage 缓存。
    // webSearchHistory 实验已全量（对齐 origin/master）：mainSlice 里的 config 字段与各处
    // enable*History 门禁均已移除，历史功能（历史下拉 / 组合历史保存）无条件常开，不再依赖 AB 拉取。

    // 会员相关 AB 测试
    abtest.fastFetchABTest({
      param_name: 'groupWebVipAB',
      default_value: {
        benifitPage: false,
        entry: false,
        chargePage: false,
        upgrade: false
      },
      value_type: 'Object',
      callback: (data) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: {
            groupWebVipAB: data
          }
        })
      }
    })

    // AI 照片王灰度（命中后 + 管理员角色：/photo/all 走新版 ai-photo-king）
    // sticky 逻辑：命中后写 localStorage，下次刷新不等 AB 接口直接显示；
    // 匿名 ID 时可能拿到 false，首次真实 user_id 就绪后通过 userId effect 补拉覆盖。
    abtest.fastFetchABTest({
      param_name: 'TheKingOfAIPhotos',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        if (res) {
          try {
            localStorage.setItem('TheKingOfAIPhotos_sticky', '1')
          } catch (_e) {
            /* localStorage may be blocked */
          }
          dispatch({
            type: actionTypes.SET_AB_TEST_CONFIG,
            data: { TheKingOfAIPhotos: true }
          })
          return
        }
        let isSticky = false
        try {
          isSticky = localStorage.getItem('TheKingOfAIPhotos_sticky') === '1'
        } catch (_e) {
          /* localStorage may be blocked */
        }
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { TheKingOfAIPhotos: isSticky }
        })
      }
    })

    // 顶部会员入口 AB：命中后顶部走新版 banner + hover 卡（见 useAbFlag('webVipEntrance')）
    abtest.fastFetchABTest({
      param_name: 'webVipEntrance',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { webVipEntrance: res }
        })
      }
    })

    // 左下角存储常驻入口 AB：命中后侧边栏展示存储入口卡（见 useAbFlag('webStorageEntrance')）
    abtest.fastFetchABTest({
      param_name: 'webStorageEntrance',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { webStorageEntrance: res }
        })
      }
    })

    // AI 填表员首页教育：神策侧只用真实交互白名单圈选未操作用户，
    // 明确排除 expose/show/eligible/blocked；前端只消费固定实验分组。
    abtest.fastFetchABTest({
      param_name: 'AI_form_filling_home_onboarding_v1',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { aiFormFillingOnboardingExperiment: res }
        })
      }
    })

    abtest.fastFetchABTest({
      param_name: 'AI_form_filling_home_hero_copy_v1',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { aiFormFillingHomeHeroCopyExperiment: res }
        })
      }
    })

    // AI 填表产品名称实验：实验组统一展示“AI 资料员”，对照组展示“AI 填表员”。
    abtest.fastFetchABTest({
      param_name: 'AI_form_filling_product_name_v1',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { aiFormFillingProductNameExperiment: res }
        })
      }
    })

    abtest.fastFetchABTest({
      param_name: 'AI_form_filling_workbench_collaboration_v1',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { aiFormFillingWorkbenchEducationExperiment: res }
        })
      }
    })

    abtest.fastFetchABTest({
      param_name: 'AI_form_filling_own_template_migration_v1',
      default_value: false,
      value_type: 'Boolean',
      callback: (res) => {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { aiFormFillingOwnTemplateExperiment: res }
        })
      }
    })
  }, [dispatch])

  // AI 版客户端老版本升级引导：低于自动更新界限的客户端打开任意 SaaS 页面时弹出。
  // 记账在弹窗组件里做——用户点关闭或下载才算处理、当天不再弹；
  // 只弹出没点按钮不记账，换页（客户端 iframe 重新加载）会继续弹。
  useEffect(() => {
    if (shouldShowAIClientUpgradeModal()) {
      setAiClientUpgradeVisible(true)
    }
  }, [])

  // 首次 mount：读 sticky 直接显示 AI 照片王入口，无需等 AB 接口返回
  useEffect(() => {
    try {
      if (localStorage.getItem('TheKingOfAIPhotos_sticky') === '1') {
        dispatch({
          type: actionTypes.SET_AB_TEST_CONFIG,
          data: { TheKingOfAIPhotos: true }
        })
      }
    } catch (_e) {
      /* localStorage may be blocked */
    }
  }, [dispatch])

  // user_id 从空到有时（首次真实登录），补拉一次 TheKingOfAIPhotos，
  // 用真实 distinct_id 分流结果覆盖之前匿名 ID 的结果。
  useEffect(() => {
    if (!userId || theKingOfAIPhotosRefetchedRef.current) return
    theKingOfAIPhotosRefetchedRef.current = true
    setTimeout(() => {
      abtest.fastFetchABTest({
        param_name: 'TheKingOfAIPhotos',
        default_value: false,
        value_type: 'Boolean',
        callback: (res) => {
          if (res) {
            try {
              localStorage.setItem('TheKingOfAIPhotos_sticky', '1')
            } catch (_e) {
              /* localStorage may be blocked */
            }
            dispatch({
              type: actionTypes.SET_AB_TEST_CONFIG,
              data: { TheKingOfAIPhotos: true }
            })
            return
          }
          let isSticky = false
          try {
            isSticky = localStorage.getItem('TheKingOfAIPhotos_sticky') === '1'
          } catch (_e) {
            /* localStorage may be blocked */
          }
          dispatch({
            type: actionTypes.SET_AB_TEST_CONFIG,
            data: { TheKingOfAIPhotos: isSticky }
          })
        }
      })
    }, 0)
  }, [userId, dispatch])

  useEffect(() => {
    if (
      !userId ||
      !workBuddyOAuthReturnURL ||
      workBuddyOAuthRedirectStartedURLRef.current === workBuddyOAuthReturnURL
    ) {
      return
    }

    workBuddyOAuthRedirectStartedURLRef.current = workBuddyOAuthReturnURL
    const redirectAttempt = registerWorkBuddyOAuthRedirectAttempt(
      workBuddyOAuthReturnURL
    )
    if (redirectAttempt !== 'allowed') {
      const reason: WorkBuddyOAuthLoginErrorReason =
        redirectAttempt === 'loop_detected'
          ? 'redirect_loop'
          : 'storage_unavailable'
      setWorkBuddyOAuthRedirectError(reason)
      // 只上报阻断原因，不携带含 state、PKCE challenge 的完整 return_url。
      sensors.track('web_workgroup_workbuddy_oauth_redirect_blocked', {
        reason
      })
      Sentry.captureMessage('WorkBuddy OAuth login redirect blocked', {
        level: Sentry.Severity.Warning,
        extra: { reason }
      })
      return
    }

    // MCP OAuth 未登录时，adapter 把完整授权请求放在 return_url 中送到本页。
    // Redux 恢复今日水印相机登录态后必须回到 WebX 桥接入口继续授权；这里使用
    // replace 避免浏览器后退再次落入登录承接页。返回地址已在工具函数中按环境、
    // origin 和固定路径做白名单校验，不能直接使用未经校验的查询参数。多团队
    // 选择由 WebX 桥接入口校验真实团队列表并渲染，前端不能自行拼接团队 ID。
    window.location.replace(workBuddyOAuthReturnURL)
  }, [userId, workBuddyOAuthReturnURL])

  // componentDidMount
  useEffect(() => {
    const queryParams = parseQueryString()
    const code = queryParams.code
    // mount effect 负责外部回调分流，必须以 effect 执行时的浏览器真实地址判断；
    // 不使用 render 闭包中的 pathname，避免后续路由调整时误消费第三方一次性 code。
    const isWorkBuddyAuthorizationPath = isWorkBuddyAuthorizePath(
      window.location.pathname
    )
    const oauthReturnURL = parseWorkBuddyOAuthReturnURL(window.location.search)
    // 微信快捷登录和 Buddy 账号绑定都使用顶层 `code`：只有授权页且不存在
    // MCP return_url 时才是 Buddy code。OAuth 登录承接页（以及修复发布前生成的
    // 旧授权页 return_url 链接）上的 code 必须继续交给微信 onekey 登录消费。
    const isWorkBuddyBuddyAuthorization =
      isWorkBuddyAuthorizationPath && !oauthReturnURL
    if (code && !isWorkBuddyBuddyAuthorization) {
      // 若当前在 iframe 内，先跳出 iframe 再继续处理回调，避免页面被嵌在微信登录 iframe 中
      if (window.top && window.top !== window.self) {
        window.top.location.href = window.location.href
        return
      }
      // 调用后端 onekey 接口完成登录
      dispatch(fetchWechatOAuth2({ token: code }))
        .then((data) => {
          if (data && data.status === 0) {
            // 清除 URL 中的 code 参数
            const url = new URL(window.location.href)
            const searchParams = new URLSearchParams(url.search)
            searchParams.delete('code')
            window.history.replaceState(
              {},
              '',
              `${url.pathname}${
                searchParams.toString() ? '?' + searchParams.toString() : ''
              }`
            )

            // 处理登录成功逻辑（与旧 OAuth2 分支一致）
            sensors.login(data.user_id)
            sensors.track('web_workgroup_wechat_pc_login_suc')
            Sentry.configureScope((scope) => {
              scope.setUser({
                id: data.user_id,
                nickname: data.nickname,
                mobile: data.mobile,
                status: data.status
              })
            })

            // 登录成功后统一交给 handleAfterLogin：内部拉取个人空间开通状态
            // 并按团队数 / 个人空间 / 已选团队决定路由（含 /my-space 豁免）。
            handleAfterLogin(data.groups || [])
          } else if (data.status === 1) {
            try {
              navigate(`/login?token=${encodeURIComponent(code)}&bind=1`, {
                replace: true
              })
            } catch (e) {
              window.location.href = `/login?token=${encodeURIComponent(
                code
              )}&bind=1`
            }
            return
          }
        })
        .catch((err) => {
          console.log('err', err)
          sensors.track('web_workgroup_wechat_pc_login_fail')
          capturePromiseException(err)
        })
      // onekey 回调处理完成，不再执行后续逻辑
      dispatch(
        fetchConfig({
          names: CONFIG.KVParams.names
        })
      )
      dispatch(getOSSToken())
      handleFetchABTestConfig()
      return
    }

    // WorkBuddy 即使携带 code 也必须先恢复今日水印相机登录态和团队列表，授权页
    // 后续会使用这两项数据展示账号并提交绑定。
    if (!code || isWorkBuddyBuddyAuthorization) {
      dispatch(fetchUser())
        .then((data) => {
          if (data.status === 0) {
            sensors.login(data.user_id)
            sensors.track('web_workgroup_keep_login_suc')
            Sentry.configureScope((scope) => {
              scope.setUser({
                id: data.user_id,
                nickname: data.nickname,
                mobile: data.mobile,
                status: data.status
              })
            })
            // 登录成功后统一交给 handleAfterLogin：内部拉取个人空间开通状态
            // 并按团队数 / 个人空间 / 已选团队决定路由。无团队时由 renderContent
            // 兜底（个人空间或验真页），多团队/扫码登录落在 /my-space 不再回弹。
            handleAfterLogin(data.groups || [])
          } else {
            sensors.track('web_workgroup_keep_login_fail', {
              reason: 'Expired'
            })
            message.error('登录失败，请重试')
          }
        })
        .catch((err) => {
          sensors.track('web_workgroup_keep_login_fail', { reason: 'others' })
          capturePromiseException(err)
        })
    }
    dispatch(
      fetchConfig({
        names: CONFIG.KVParams.names
      })
    )
    dispatch(getOSSToken())
    handleFetchABTestConfig()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const NotFoundPage = () => {
    return (
      <div className="result-page-wrapper">
        <Result
          status="404"
          title="404"
          subTitle="抱歉, 页面不存在。"
          extra={
            <Button
              type="primary"
              onClick={() => navigate('/choose_group', { replace: true })}
            >
              返回首页
            </Button>
          }
        />
      </div>
    )
  }

  const renderContent = () => {
    if (pathname === '/1') {
      return <GeoData />
    }

    // OAuth 登录承接页必须在登录页和通用业务路由之前校验。缺失 return_url 与
    // 白名单拒绝使用不同文案，避免错误请求落入 404、个人空间或验真页。
    if (isWorkBuddyOAuthLoginPage && !workBuddyOAuthReturnURL) {
      return (
        <WorkBuddyOAuthLoginError
          reason={
            rawWorkBuddyOAuthReturnURL
              ? 'untrusted_return_url'
              : 'missing_return_url'
          }
        />
      )
    }
    if (!user) {
      return <Login onFetchGroupInfo={handleFetchGroupInfo} />
    }
    if (workBuddyOAuthRedirectError) {
      return <WorkBuddyOAuthLoginError reason={workBuddyOAuthRedirectError} />
    }
    // 登录态已经恢复且存在可信的 MCP OAuth 返回地址时，重定向 effect 会立即接管。
    // 等待期间不渲染 Buddy code 页面，避免把 return_url 流程误报成“授权链接无效”。
    if (workBuddyOAuthReturnURL) {
      return <RouteLoading />
    }
    // 登录态确认后优先进入授权页，避免通用团队路由覆盖 WorkBuddy 一次性 code。
    if (isWorkBuddyAuthorizePage) {
      return <WorkBuddyAuthorize />
    }
    if (pathname === '/choose_group') {
      // 等个人空间信息就绪后再渲染，确保 isSpaceOpen 已更新，个人空间入口不闪烁
      if (!isSpaceInfoLoaded) return null
      return <ChooseGroup />
    }

    if (pathname.startsWith('/my-space')) {
      return (
        <Routes>
          <Route path="/my-space/*" element={<MySpaceRoutes />} />
        </Routes>
      )
    }

    if (initialRequestFinished && !groupName) {
      // 个人空间信息未就绪（space/info 未返回）→ 等待，不渲染任何业务页
      if (!isSpaceInfoLoaded) return null
      // groupID 已设置 → 有团队，fetchGroupInfo 正在加载，等待
      if (groupID) return null
      // 无团队有个人空间 → 跳个人空间（但验真页需放行）
      if (isSpaceOpen) {
        if (
          !pathname.startsWith('/verifyAuth') &&
          !pathname.startsWith('/verification')
        ) {
          return <Navigate to="/my-space/personal-space" replace />
        }
      }
      // 确认无团队无个人空间，展示验真页
      return (
        <MainWrapper>
          <Routes>
            <Route path="/verification" element={<VerifyAuth />} />
            <Route path="/verifyAuth/index" element={<VerifyAuth />} />
            <Route
              path="/verifyAuth/process/:id/:num"
              element={<VerifyProcess />}
            />
            <Route
              path="/verifyAuth/manual-review"
              element={<VerifyManualReview />}
            />
            <Route path="/verifyAuth/result" element={<VerifyResult />} />
            <Route path="/" element={<VerifyAuth />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </MainWrapper>
      )
    }

    if (initialRequestFinished && pathname === '/privileges') {
      return <Privileges />
    }

    if (initialRequestFinished && groupAuthPolicy) {
      // 必须数据请求完成
      return (
        <MainWrapper>
          <Routes>
            <Route
              path="/data/statistics"
              element={
                <AdminRoleGuard>
                  <DataStatistics />
                </AdminRoleGuard>
              }
            />
            <Route path="/photo/all" element={<AIPhotoKing />} />
            <Route
              path="/three-inspection/templates"
              element={
                <ThreeInspectionGuard>
                  <ThreeInspectionTemplates />
                </ThreeInspectionGuard>
              }
            />
            <Route
              path="/three-inspection/report/new"
              element={
                <ThreeInspectionGuard>
                  <ThreeInspectionEditor />
                </ThreeInspectionGuard>
              }
            />
            {/* 拍照流程后台一期：仅对已放量团队（taskForm.enabled）放行，未放量重定向回全部照片 */}
            <Route
              path="/photo/task-flow/manage"
              element={
                <TaskFlowGuard>
                  <PhotoTaskFlowManage />
                </TaskFlowGuard>
              }
            />
            <Route
              path="/photo/task-flow"
              element={
                <TaskFlowGuard>
                  <PhotoTaskFlow />
                </TaskFlowGuard>
              }
            />
            <Route path="/ai/photo/search" element={<AiSearchPhoto />} />
            <Route path="/photo/jigsaw" element={<Jigsaw />} />
            <Route path="/photo/ai-tags" element={<AIPhotoTags />} />
            <Route path="/photo/ai" element={<AIPhoto />} />
            <Route path="/ai/duplicate-photo" element={<AIDuplicatePhoto />} />
            <Route path="/today-claw" element={<TodayClaw />} />
            <Route path="/photo/delete-records" element={<DeleteRecords />} />
            <Route
              path="/photo/download-failed-photos"
              element={<DownloadFailedPhotos />}
            />
            <Route path="/verification" element={<VerifyAuth />} />
            <Route path="/verifyAuth/index" element={<VerifyAuth />} />
            <Route
              path="/verifyAuth/process/:id/:num"
              element={<VerifyProcess />}
            />
            <Route
              path="/verifyAuth/manual-review"
              element={<VerifyManualReview />}
            />
            <Route path="/verifyAuth/result" element={<VerifyResult />} />
            {/* 分类文件夹已全量切到 AI 智能分类，旧列表入口重定向到新版 */}
            <Route
              path="/photo/folders"
              element={<Navigate to="/ai-classify" replace />}
            />
            {/* 旧分类文件夹详情重定向到 AI 智能分类详情，兼容历史链接/书签 */}
            <Route
              path="/photo/folder/detail/:id"
              element={<PhotoFolderDetailRedirect />}
            />
            <Route
              path="/photo/folder/add/:id"
              element={<PhotoFolderAddPhoto />}
            />
            <Route
              path="/ai-classify"
              element={
                <AIClassifyFolderGuard>
                  <AIClassifyFolder />
                </AIClassifyFolderGuard>
              }
            />
            <Route
              path="/ai-classify/unclassified"
              element={
                <AIClassifyFolderGuard>
                  <AIClassifyUnclassified />
                </AIClassifyFolderGuard>
              }
            />
            <Route
              path="/ai-classify/folder/:folderId"
              element={
                <AIClassifyFolderGuard>
                  <AIClassifyFolderDetail />
                </AIClassifyFolderGuard>
              }
            />
            <Route path="/mingyang-ai/analysis" element={<MingyangAi />} />
            <Route path="/photo/picker" element={<PhotoPickerPage />} />
            <Route path="/photo/location" element={<PhotoLocation />} />
            <Route
              path="/member/location"
              element={
                <AdminRoleGuard>
                  <MemberLocation />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/member/track"
              element={
                <AdminRoleGuard>
                  <MemberTrack />
                </AdminRoleGuard>
              }
            />
            <Route path="/rectify/list" element={<Rectify />} />
            <Route
              path="/pathway"
              element={
                <AdminRoleGuard>
                  <Pathway />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/attendance/daily"
              element={
                <AdminRoleGuard>
                  <AttendanceDaily />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/attendance/period"
              element={
                <AdminRoleGuard>
                  <AttendancePeriod />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/attendance/ruleset"
              element={
                <AdminRoleGuard>
                  <AttendanceSetting />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/attendance/rulesets"
              element={
                <AdminRoleGuard>
                  <AttendanceSettings />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/attendance/calendar"
              element={
                <AdminRoleGuard>
                  <AttendanceCalendar />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/attendance/export-list"
              element={<AttendanceExportList />}
            />
            <Route path="/watermark/data" element={<PhotoWatermark />} />
            <Route path="/ai/security" element={<AISecurityLedger />} />
            <Route
              path="/ai/recognition/export-list"
              element={<AIRecognitionExportList />}
            />
            <Route path="/ai/recognition" element={<AIRecognition />} />
            <Route
              path="/watermark/list"
              element={
                <WatermarkAdminGuard>
                  <WatermarkList />
                </WatermarkAdminGuard>
              }
            />
            <Route
              path="/watermark/editor"
              element={
                <WatermarkAdminGuard>
                  <WatermarkEditor />
                </WatermarkAdminGuard>
              }
            />
            <Route
              path="/watermark/batchEditor/add"
              element={<AddBatchEditPhotos />}
            />
            <Route
              path="/watermark/batchEditor/test"
              element={<WatermarkBatchEditorTest />}
            />
            <Route
              path="/watermark/batchEditor"
              element={<WatermarkBatchEditor />}
            />
            <Route
              path="/team/member/import"
              element={
                <AdminRoleGuard>
                  <TeamBatchImport />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/team/member/org-arc-import"
              element={
                <AdminRoleGuard>
                  <OrgArcBatchImort />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/team/department"
              element={
                <AdminRoleGuard>
                  <Department />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/team/admin"
              element={
                <AdminRoleGuard>
                  <Admin />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/team/log"
              element={
                <AdminRoleGuard>
                  <TeamOperationLog />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/visit/record"
              element={
                <AuthPolicyWrapper>
                  <CustomerVisitRecords />
                </AuthPolicyWrapper>
              }
            />
            <Route
              path="/customer/info"
              element={
                <AuthPolicyWrapper>
                  <Customers />
                </AuthPolicyWrapper>
              }
            />
            <Route
              path="/authority"
              element={
                <AdminRoleGuard>
                  <Authority />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/credit/report/:phone"
              element={
                <AdminRoleGuard>
                  <CreditReport />
                </AdminRoleGuard>
              }
            />
            <Route
              path="/credit"
              element={
                <AdminRoleGuard>
                  <CreditHome />
                </AdminRoleGuard>
              }
            />
            <Route path="/orders" element={<Orders />} />
            <Route path="/myse/simulate" element={<MyseSimulate />} />
            <Route
              path="/myse/knowledge-base"
              element={<MyseKnowledgeBase />}
            />
            <Route path="/myse/check-records" element={<MyseCheckRecords />} />
            <Route path="/myse/projects" element={<MyseProjects />} />
            <Route path="/myse/downloads" element={<MyseDownloads />} />
            <Route path="/native-claw-pro" element={<NativeClawProPage />} />
            <Route path="/embed/vip" element={<EmbedVipPage />} />
            <Route path="/qa" element={<QA />} />
            <Route path="/userManual" element={<UserManual />} />
            <Route path="/ai/clerk/*" element={<AIClerk />} />
            <Route path="/photo" element={<AIPhotoKing />} />
            <Route path="/" element={<AIPhotoKing />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </MainWrapper>
      )
    }

    return null
  }

  // 该弹窗渲染在 renderContent 外层，除授权页外会沿用全局条件出现在登录后的页面；
  // 点击“开始使用”会跳转 /today-claw，而 WorkBuddy 授权页依赖一次性 code，
  // 因此必须显式排除，不能只依赖业务内容路由守卫。
  const clawBetaModalVisible =
    !isWorkBuddyAuthorizePage &&
    !isWorkBuddyOAuthLoginPage &&
    shouldShowClawBetaApprovedModal({
      user,
      groupId: groupID,
      teamShortId,
      myRole,
      mainConfig,
      modalClosed: clawBetaModalClosed
    })

  return (
    <div className="App">
      <Suspense fallback={<RouteLoading />}>{renderContent()}</Suspense>
      <AgentCustomerDrawer />
      <ClawBetaApprovedModal
        visible={clawBetaModalVisible}
        onStart={() => {
          markClawBetaApprovedShown(teamShortId)
          setClawBetaModalClosed(true)
          navigate('/today-claw', { replace: true })
        }}
        onClose={() => {
          markClawBetaApprovedShown(teamShortId)
          setClawBetaModalClosed(true)
        }}
      />
      <AIClientUpgradeModal
        visible={aiClientUpgradeVisible}
        onClose={() => setAiClientUpgradeVisible(false)}
      />
    </div>
  )
}

export default App
